import { makeAutoObservable } from 'mobx'

import { tutorialHintEndPositions, tutorialHintStartPositions } from '../game/config/constants'

/** Control-hint groups: hand + arrow share one visibility flag. */
export type TutorialHintGroup = 'left' | 'right' | 'jump' | 'snap'

const HINT_REQUIRED_LANE_INDEX: Partial<Record<TutorialHintGroup, number>> = {
    right: 1,
    left: 2,
    jump: 1,
}

/** MobX store for independent tutorial hint visibility. */
export class TutorialStore {
    active = false

    hintLeftVisible = false
    hintRightVisible = false
    hintJumpVisible = false
    hintSnapVisible = false

    private dismissed: Record<TutorialHintGroup, boolean> = {
        left: false,
        right: false,
        jump: false,
        snap: false,
    }

    private currentTrackDistance = 0
    private currentLaneIndex = 1

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    isGroupVisible(group: TutorialHintGroup): boolean {
        switch (group) {
            case 'left':
                return this.hintLeftVisible
            case 'right':
                return this.hintRightVisible
            case 'jump':
                return this.hintJumpVisible
            case 'snap':
                return this.hintSnapVisible
        }
    }

    /** Whether a hint group should render (tutorial on, step active, distance reached). */
    isHintDisplayed(
        group: TutorialHintGroup,
        ctx: { tutorialEnabled: boolean; trackDistance: number },
    ): boolean {
        if (!ctx.tutorialEnabled) return false
        if (!this.isGroupVisible(group)) return false
        return (
            this.hasReachedHintStart(group, ctx.trackDistance) &&
            !this.hasPassedHintEnd(group, ctx.trackDistance) &&
            this.isInRequiredLane(group)
        )
    }

    setHintGroupVisible(group: TutorialHintGroup, visible: boolean) {
        switch (group) {
            case 'left':
                this.hintLeftVisible = visible
                break
            case 'right':
                this.hintRightVisible = visible
                break
            case 'jump':
                this.hintJumpVisible = visible
                break
            case 'snap':
                this.hintSnapVisible = visible
                break
        }
    }

    private hideAllHints() {
        this.hintLeftVisible = false
        this.hintRightVisible = false
        this.hintJumpVisible = false
        this.hintSnapVisible = false
    }

    private hasReachedHintStart(group: TutorialHintGroup, trackDistance: number) {
        return trackDistance >= tutorialHintStartPositions[group]
    }

    private hasPassedHintEnd(group: TutorialHintGroup, trackDistance: number) {
        return trackDistance >= tutorialHintEndPositions[group]
    }

    private isInRequiredLane(group: TutorialHintGroup) {
        const requiredLaneIndex = HINT_REQUIRED_LANE_INDEX[group]
        return requiredLaneIndex == null || this.currentLaneIndex === requiredLaneIndex
    }

    private isHintActive(group: TutorialHintGroup) {
        return (
            this.isGroupVisible(group) &&
            this.hasReachedHintStart(group, this.currentTrackDistance) &&
            !this.hasPassedHintEnd(group, this.currentTrackDistance) &&
            this.isInRequiredLane(group)
        )
    }

    private dismissHint(group: TutorialHintGroup) {
        this.dismissed[group] = true
        this.setHintGroupVisible(group, false)
    }

    /** Toggle snap hint visibility when the snap action is currently available. */
    setSnapHintAvailable(available: boolean) {
        if (!this.active) return
        if (this.dismissed.snap || this.hasPassedHintEnd('snap', this.currentTrackDistance)) {
            this.setHintGroupVisible('snap', false)
            return
        }
        this.setHintGroupVisible('snap', available)
    }

    syncTrackDistance(trackDistance: number) {
        this.currentTrackDistance = trackDistance

        if (!this.active) return

        if (this.hintLeftVisible && this.hasPassedHintEnd('left', trackDistance)) {
            this.dismissHint('left')
        }
        if (this.hintRightVisible && this.hasPassedHintEnd('right', trackDistance)) {
            this.dismissHint('right')
        }
        if (this.hintJumpVisible && this.hasPassedHintEnd('jump', trackDistance)) {
            this.dismissHint('jump')
        }
        if (this.hintSnapVisible && this.hasPassedHintEnd('snap', trackDistance)) {
            this.dismissHint('snap')
        }
    }

    syncLaneIndex(laneIndex: number) {
        this.currentLaneIndex = laneIndex
    }

    /** Begin tutorial when a run starts. */
    startRun() {
        this.active = true
        this.currentLaneIndex = 1
        this.dismissed.left = false
        this.dismissed.right = false
        this.dismissed.jump = false
        this.dismissed.snap = false
        this.hintLeftVisible = !this.hasPassedHintEnd('left', this.currentTrackDistance)
        this.hintRightVisible = !this.hasPassedHintEnd('right', this.currentTrackDistance)
        this.hintJumpVisible = !this.hasPassedHintEnd('jump', this.currentTrackDistance)
        this.hintSnapVisible = false
    }

    /** Clear tutorial state (run reset / tutorial disabled). */
    reset() {
        this.active = false
        this.currentTrackDistance = 0
        this.currentLaneIndex = 1
        this.dismissed.left = false
        this.dismissed.right = false
        this.dismissed.jump = false
        this.dismissed.snap = false
        this.hideAllHints()
    }

    notifyMoveLeft() {
        if (!this.active) return
        if (!this.isHintActive('left')) return
        this.dismissHint('left')
    }

    notifyMoveRight() {
        if (!this.active) return
        if (!this.isHintActive('right')) return
        this.dismissHint('right')
    }

    notifyJump() {
        if (!this.active) return
        if (!this.isHintActive('jump')) return
        this.dismissHint('jump')
    }

    notifySnap() {
        if (!this.active) return
        if (!this.isHintActive('snap')) return
        this.dismissHint('snap')
    }
}
